import * as Sentry from "@sentry/react"
import { ConfigProvider } from "antd"
import en_US from "antd/lib/locale/en_US"
import de_DE from "antd/lib/locale/de_DE"
import Error from "Components/layout/error"
import ErrorPage from "Features/errors/ErrorPage"
import moment from "moment"
import React, { useEffect } from "react"
import { Helmet } from "react-helmet"
import { Provider } from "react-redux"
import { BrowserRouter as Router } from "react-router-dom"
import { Store } from "redux"
import { PersistGate } from "redux-persist/integration/react"
import AxiosInterceptor from "Shared/config/axios.interceptor"
import { ModalContextConsumer, ModalContextProvider } from "Shared/context/modal.context"
import { useLoadWeavr } from "Shared/hooks/weavr.hooks"
import i18n from "Shared/locales/i18n"
import AppRouter from "Shared/router/AppRouter"
import store, { persistor } from "Shared/store"
import { RootState } from "Shared/store/rootState.interface"
import { configLocaleNumberFormatting, getAntdLocale } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import "../node_modules/react-vis/dist/style.css"
import "./App.scss"
import { Backend as MockBackend } from "./mock/backend"
import { Worker } from "@react-pdf-viewer/core"
import { COMMIT } from "Shared/config/consts"
interface AppInterface {
    store: Store<RootState>
}

const App: React.FC<AppInterface> = (props) => {

    if (process.env.NODE_ENV === "test") {
        const backend: MockBackend = new MockBackend()
        backend.start()
    }
    const [locale, setLocale] = useStateIfMounted(en_US)

    useEffect(() => {
        console.log(`===== APP version ${COMMIT} =====`)
        AxiosInterceptor.setupInterceptors()
    }, [])

    useLoadWeavr()

    i18n.on("languageChanged", (lng) => {
        configLocaleNumberFormatting(lng)
        moment.locale(lng)
        setLocale(getAntdLocale(lng))
    })

    const ErrorHandler = (props: any) => (
        <Error>
            <ErrorPage {...props} />
        </Error>
    ) 

    return (
        <Provider store={store}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.12.313/build/pdf.worker.min.js">
                <Helmet titleTemplate="%s - finway" defaultTitle="Finway">
                    <meta charSet="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
                    <link rel="icon" href="./images/favicons/32x32.png" sizes="32x32" />
                    <link rel="icon" href="./images/favicons/192x192.png" sizes="192x192" />
                    <link rel="apple-touch-icon-precomposed" href="./images/favicons/180x180.png" />
                    <meta name="msapplication-TileImage" content="./images/favicons/310x310.png" />
                </Helmet>
                <Router>
                    <Sentry.ErrorBoundary fallback={ErrorHandler}>
                        <PersistGate loading={null} persistor={persistor}>
                            <ConfigProvider locale={locale}>
                                <ModalContextProvider>
                                    <ModalContextConsumer />
                                    <AppRouter />
                                </ModalContextProvider>
                            </ConfigProvider>
                        </PersistGate>
                    </Sentry.ErrorBoundary>
                </Router>
            </Worker>
        </Provider>
    )
}

export default App
